import { useEffect, useRef, useState, useCallback } from 'react';

const WS_BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export function useWebSocket(path, token, options = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = options.maxReconnectAttempts || 5;

  const connect = useCallback(() => {
    if (!token || !path) return;
    
    const url = `${WS_BASE_URL}${path}?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(url);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected:', path);
        setIsConnected(true);
        reconnectAttempts.current = 0;
        options.onOpen?.();
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          options.onMessage?.(data);
        } catch (e) {
          console.error('Failed to parse WS message:', e);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        options.onError?.(error);
      };
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        options.onClose?.(event);
        
        // Auto-reconnect logic
        if (reconnectAttempts.current < maxReconnectAttempts && !event.wasClean) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [path, token, options, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    reconnect: connect,
    disconnect
  };
}

// Hook for chat-specific WebSocket
export function useChatWebSocket(token, onMessage) {
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const typingTimeouts = useRef({});

  const ws = useWebSocket('/ws/chat', token, {
    onMessage: (data) => {
      switch (data.type) {
        case 'chat_message':
        case 'message_sent':
          onMessage?.(data);
          break;
        
        case 'typing':
          setTypingUsers(prev => ({
            ...prev,
            [data.user_id]: data.is_typing
          }));
          
          // Clear typing indicator after 3 seconds
          if (data.is_typing) {
            if (typingTimeouts.current[data.user_id]) {
              clearTimeout(typingTimeouts.current[data.user_id]);
            }
            typingTimeouts.current[data.user_id] = setTimeout(() => {
              setTypingUsers(prev => ({ ...prev, [data.user_id]: false }));
            }, 3000);
          }
          break;
        
        case 'read_receipt':
          onMessage?.({ type: 'read_receipt', ...data });
          break;
        
        case 'status_update':
          setOnlineUsers(prev => ({
            ...prev,
            [data.user_id]: data.online
          }));
          break;
        
        case 'status_response':
          setOnlineUsers(prev => ({
            ...prev,
            ...Object.fromEntries(
              Object.entries(data.statuses).map(([id, status]) => [id, status.online])
            )
          }));
          break;
        
        default:
          console.log('Unknown message type:', data.type);
      }
    }
  });

  const sendChatMessage = useCallback((receiverId, content) => {
    return ws.sendMessage({
      type: 'chat_message',
      receiver_id: receiverId,
      content
    });
  }, [ws]);

  const sendTypingIndicator = useCallback((receiverId, isTyping) => {
    return ws.sendMessage({
      type: 'typing',
      receiver_id: receiverId,
      is_typing: isTyping
    });
  }, [ws]);

  const sendReadReceipt = useCallback((senderId, messageIds) => {
    return ws.sendMessage({
      type: 'read_receipt',
      sender_id: senderId,
      message_ids: messageIds
    });
  }, [ws]);

  const getOnlineStatus = useCallback((userIds) => {
    return ws.sendMessage({
      type: 'get_status',
      user_ids: userIds
    });
  }, [ws]);

  return {
    ...ws,
    typingUsers,
    onlineUsers,
    sendChatMessage,
    sendTypingIndicator,
    sendReadReceipt,
    getOnlineStatus
  };
}

// Hook for video call WebSocket
export function useVideoWebSocket(roomId, token, callbacks = {}) {
  const ws = useWebSocket(`/ws/video/${roomId}`, token, {
    onMessage: (data) => {
      switch (data.type) {
        case 'offer':
          callbacks.onOffer?.(data);
          break;
        case 'answer':
          callbacks.onAnswer?.(data);
          break;
        case 'candidate':
          callbacks.onCandidate?.(data);
          break;
        case 'user_joined':
          callbacks.onUserJoined?.(data);
          break;
        case 'user_left':
          callbacks.onUserLeft?.(data);
          break;
        case 'chat':
          callbacks.onChat?.(data);
          break;
        case 'mute_status':
          callbacks.onMuteStatus?.(data);
          break;
        default:
          console.log('Unknown video message:', data.type);
      }
    }
  });

  const sendOffer = useCallback((offer) => {
    return ws.sendMessage({ type: 'offer', offer });
  }, [ws]);

  const sendAnswer = useCallback((answer) => {
    return ws.sendMessage({ type: 'answer', answer });
  }, [ws]);

  const sendCandidate = useCallback((candidate) => {
    return ws.sendMessage({ type: 'candidate', candidate });
  }, [ws]);

  const sendMuteStatus = useCallback((audioMuted, videoMuted) => {
    return ws.sendMessage({
      type: 'mute',
      audio_muted: audioMuted,
      video_muted: videoMuted
    });
  }, [ws]);

  const sendChat = useCallback((content) => {
    return ws.sendMessage({ type: 'chat', content });
  }, [ws]);

  return {
    ...ws,
    sendOffer,
    sendAnswer,
    sendCandidate,
    sendMuteStatus,
    sendChat
  };
}
